import React, { useState, useEffect } from 'react';
import { api } from '../../utils/api';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Plus, Trash2, GripVertical, Save, FolderPlus } from 'lucide-react';

const PRODUCTS = [
  { id: 'photobox-eco', label: 'Eco Fotobox', color: 'bg-blue-100 text-blue-800' },
  { id: 'photobox-premium', label: 'Premium Fotobox', color: 'bg-green-100 text-green-800' },
  { id: 'photobox-business', label: 'Business Fotobox', color: 'bg-amber-100 text-amber-800' },
];

export function FeaturesPage() {
  const [features, setFeatures] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [showGroupsModal, setShowGroupsModal] = useState(false);

  useEffect(() => {
    loadFeatures();
  }, []);

  const loadFeatures = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/features');

      // Ensure customTexts is initialized for each feature
      const featuresWithCustomTexts = (response.features || []).map(feature => ({
        ...feature,
        customTexts: feature.customTexts || {}
      }));

      setFeatures(featuresWithCustomTexts);
      setGroups(response.groups || []);
      setHasChanges(false);
    } catch (error) {
      console.error('Error loading features:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddFeature = () => {
    const newFeature = {
      id: null, // New feature has no ID
      name: '',
      displayOrder: features.length,
      groupId: null,
      products: {
        'photobox-eco': false,
        'photobox-premium': false,
        'photobox-business': false,
      },
      customTexts: {
        'photobox-eco': '',
        'photobox-premium': '',
        'photobox-business': '',
      },
    };
    setFeatures([...features, newFeature]);
    setHasChanges(true);
  };

  const handleAddGroup = () => {
    const newGroup = {
      id: null,
      name: '',
      display_order: groups.length,
    };
    setGroups([...groups, newGroup]);
    setHasChanges(true);
  };

  const handleDeleteGroup = async (index, groupId) => {
    if (groupId) {
      if (!confirm('Möchten Sie diese Gruppe wirklich löschen? Features in dieser Gruppe werden zu "Ohne Gruppe".')) {
        return;
      }
      try {
        await api.delete(`/admin/features/groups/${groupId}`);
        await loadFeatures();
      } catch (error) {
        console.error('Error deleting group:', error);
        alert('Fehler beim Löschen der Gruppe');
      }
    } else {
      // New unsaved group
      const updated = groups.filter((_, i) => i !== index);
      setGroups(updated);
      setHasChanges(true);
    }
  };

  const handleGroupNameChange = (index, name) => {
    const updated = [...groups];
    updated[index].name = name;
    setGroups(updated);
    setHasChanges(true);
  };

  const handleFeatureGroupChange = (featureIndex, groupId) => {
    const updated = [...features];
    updated[featureIndex].groupId = groupId === '' ? null : parseInt(groupId);
    setFeatures(updated);
    setHasChanges(true);
  };

  const handleDeleteFeature = async (index, featureId) => {
    if (featureId) {
      // Existing feature - delete from backend
      if (!confirm('Möchten Sie dieses Feature wirklich löschen?')) {
        return;
      }
      try {
        await api.delete(`/admin/features/${featureId}`);
        await loadFeatures();
      } catch (error) {
        console.error('Error deleting feature:', error);
        alert('Fehler beim Löschen des Features');
      }
    } else {
      // New unsaved feature - just remove from list
      const updated = features.filter((_, i) => i !== index);
      setFeatures(updated);
      setHasChanges(true);
    }
  };

  const handleFeatureNameChange = (index, name) => {
    const updated = [...features];
    updated[index].name = name;
    setFeatures(updated);
    setHasChanges(true);
  };

  const handleProductToggle = (featureIndex, productId) => {
    const updated = [...features];
    const currentValue = updated[featureIndex].products[productId] || false;
    updated[featureIndex].products[productId] = !currentValue;
    setFeatures(updated);
    setHasChanges(true);
  };

  const handleCustomTextChange = (featureIndex, productId, text) => {
    const updated = [...features];
    if (!updated[featureIndex].customTexts) {
      updated[featureIndex].customTexts = {};
    }
    updated[featureIndex].customTexts[productId] = text;
    setFeatures(updated);
    setHasChanges(true);
  };

  const handleSaveFeatures = async () => {
    // Validate: all features must have names
    const invalid = features.find(f => !f.name || f.name.trim() === '');
    if (invalid) {
      alert('Bitte geben Sie allen Features einen Namen.');
      return;
    }

    // Validate: all groups must have names
    const invalidGroup = groups.find(g => !g.name || g.name.trim() === '');
    if (invalidGroup) {
      alert('Bitte geben Sie allen Gruppen einen Namen.');
      return;
    }

    try {
      setSaving(true);

      // Save groups first
      if (groups.length > 0) {
        await api.post('/admin/features/groups', { groups });
      }

      // Then save features
      await api.post('/admin/features', { features });

      await loadFeatures();
    } catch (error) {
      console.error('Error saving features:', error);
      alert('Fehler beim Speichern der Features');
    } finally {
      setSaving(false);
    }
  };

  const moveFeature = (index, direction) => {
    if (
      (direction === -1 && index === 0) ||
      (direction === 1 && index === features.length - 1)
    ) {
      return;
    }

    const updated = [...features];
    const temp = updated[index];
    updated[index] = updated[index + direction];
    updated[index + direction] = temp;
    setFeatures(updated);
    setHasChanges(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Lade Features...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Feature-Verwaltung</h1>
          <p className="mt-1 text-sm text-gray-500">
            Verwalten Sie Produktmerkmale und ihre Zuordnung zu Fotobox-Paketen
          </p>
        </div>
        <div className="flex gap-3">
          <Button
            onClick={() => setShowGroupsModal(true)}
            variant="outline"
            className="flex items-center gap-2"
          >
            <FolderPlus className="w-4 h-4" />
            Gruppen verwalten
          </Button>
          <Button
            onClick={handleAddFeature}
            variant="secondary"
            className="flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Feature hinzufügen
          </Button>
          {hasChanges && (
            <Button
              onClick={handleSaveFeatures}
              disabled={saving}
              className="flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Speichern...' : 'Änderungen speichern'}
            </Button>
          )}
        </div>
      </div>

      {hasChanges && (
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-sm text-yellow-800">
            <strong>Ungespeicherte Änderungen:</strong> Vergessen Sie nicht, Ihre Änderungen zu speichern.
          </p>
        </div>
      )}

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-700 uppercase w-12">
                  <GripVertical className="w-4 h-4 text-gray-400" />
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                  Feature Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                  Gruppe
                </th>
                {PRODUCTS.map(product => (
                  <th key={product.id} className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase">
                    {product.label}
                  </th>
                ))}
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase w-24">
                  Aktionen
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {features.length === 0 ? (
                <tr>
                  <td colSpan={6 + PRODUCTS.length} className="px-6 py-12 text-center text-gray-500">
                    <p className="text-lg">Keine Features vorhanden</p>
                    <p className="text-sm mt-2">Klicken Sie auf "Feature hinzufügen", um zu beginnen.</p>
                  </td>
                </tr>
              ) : (
                features.map((feature, index) => (
                  <tr key={feature.id || `new-${index}`} className="hover:bg-gray-50">
                    <td className="px-3 py-4">
                      <div className="flex flex-col gap-1">
                        <button
                          onClick={() => moveFeature(index, -1)}
                          disabled={index === 0}
                          className="text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                          title="Nach oben"
                        >
                          ▲
                        </button>
                        <button
                          onClick={() => moveFeature(index, 1)}
                          disabled={index === features.length - 1}
                          className="text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                          title="Nach unten"
                        >
                          ▼
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <input
                        type="text"
                        value={feature.name}
                        onChange={(e) => handleFeatureNameChange(index, e.target.value)}
                        placeholder="Feature-Name eingeben..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </td>
                    <td className="px-4 py-4">
                      <select
                        value={feature.groupId || ''}
                        onChange={(e) => handleFeatureGroupChange(index, e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      >
                        <option value="">Ohne Gruppe</option>
                        {groups.map(group => (
                          <option key={group.id} value={group.id}>
                            {group.name}
                          </option>
                        ))}
                      </select>
                    </td>
                    {PRODUCTS.map(product => (
                      <td key={product.id} className="px-4 py-4">
                        <div className="flex flex-col gap-2">
                          <div className="flex items-center justify-center">
                            <input
                              type="checkbox"
                              checked={feature.products[product.id] || false}
                              onChange={() => handleProductToggle(index, product.id)}
                              className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                            />
                          </div>
                          {feature.products[product.id] && (
                            <input
                              type="text"
                              value={feature.customTexts?.[product.id] || ''}
                              onChange={(e) => handleCustomTextChange(index, product.id, e.target.value)}
                              placeholder="Text (optional)"
                              className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                          )}
                        </div>
                      </td>
                    ))}
                    <td className="px-4 py-4 text-center">
                      <button
                        onClick={() => handleDeleteFeature(index, feature.id)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                        Löschen
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {features.length > 0 && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>Tipp:</strong> Die Reihenfolge der Features bestimmt, wie sie auf der Website angezeigt werden.
            Verwenden Sie die Pfeile, um die Reihenfolge zu ändern.
          </p>
        </div>
      )}

      {/* Groups Management Modal */}
      {showGroupsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">Feature-Gruppen verwalten</h2>
              <p className="mt-1 text-sm text-gray-500">
                Gruppieren Sie Features für eine bessere Organisation auf der Landing Page
              </p>
            </div>

            <div className="p-6">
              <div className="space-y-4">
                {groups.map((group, index) => (
                  <div key={group.id || `new-${index}`} className="flex items-center gap-3">
                    <input
                      type="text"
                      value={group.name}
                      onChange={(e) => handleGroupNameChange(index, e.target.value)}
                      placeholder="Gruppenname eingeben..."
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                      onClick={() => handleDeleteGroup(index, group.id)}
                      className="px-3 py-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                ))}

                {groups.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <p>Keine Gruppen vorhanden</p>
                    <p className="text-sm mt-1">Klicken Sie auf "Gruppe hinzufügen", um zu beginnen.</p>
                  </div>
                )}

                <Button
                  onClick={handleAddGroup}
                  variant="secondary"
                  className="w-full flex items-center justify-center gap-2 mt-4"
                >
                  <Plus className="w-4 h-4" />
                  Gruppe hinzufügen
                </Button>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <Button
                onClick={() => setShowGroupsModal(false)}
                variant="outline"
              >
                Schließen
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
